/**
 * PATH: packages/ui/src/components/find/useInPageHighlight.ts
 * FILE_ID: UI-FIND-HIGHLIGHT-HOOK-001
 * PURPOSE: React orchestration for the in-page find highlight. Applies
 *          highlights over a container, keeps them in sync when the
 *          host re-renders the body (inline-image recovery / thread
 *          hydration re-set innerHTML), tracks the active hit + total
 *          count, and scrolls the active hit into view.
 * WHY:     The body is rendered via dangerouslySetInnerHTML, so React
 *          re-applies the WHOLE innerHTML on an html change (wiping our
 *          highlights). A MutationObserver re-applies after such changes.
 *          The painter behind this hook is mutation-free where the CSS
 *          Custom Highlight API exists; where it isn't, apply() writes
 *          <mark>s, so we disconnect around our OWN mutations to avoid a
 *          feedback loop.
 * FLOW:
 *   (containerRef, query, active) → painter.apply → count
 *      + activeIndex → painter.setActive (paints + scrolls) ; next/prev cycle.
 * DEPENDENCIES:
 *   - react: state + effects.
 *   - ./findHighlightPainter: strategy + capability selection.
 */
import { useCallback, useEffect, useRef, useState, type RefObject } from 'react'
import {
  createFindPainter,
  type FindHighlightPainter,
} from './findHighlightPainter'

export interface InPageHighlightApi {
  /** Total number of hits currently highlighted. */
  count: number
  /** Zero-based index of the active hit (0 when count is 0). */
  activeIndex: number
  /** Advance to the next hit (wraps). */
  next: () => void
  /** Go to the previous hit (wraps). */
  prev: () => void
}

/**
 * Highlight `query` inside `containerRef` while `active`. Returns the
 * hit count + active index + next/prev cyclers. When `active` is false
 * or the query is blank, the container is left clean.
 */
export function useInPageHighlight(
  containerRef: RefObject<HTMLElement | null>,
  query: string,
  active: boolean,
): InPageHighlightApi {
  const [count, setCount] = useState(0)
  const [activeIndex, setActiveIndex] = useState(0)
  const painterRef = useRef<FindHighlightPainter | null>(null)

  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    const painter = painterRef.current ?? createFindPainter()
    painterRef.current = painter

    if (!active || !query.trim()) {
      painter.clear(container)
      setCount(0)
      setActiveIndex(0)
      return
    }

    // One observer for the container. A DOM-mutating painter disconnects
    // around its own writes so re-highlighting never re-triggers itself;
    // external mutations (React re-setting innerHTML) DO re-trigger it —
    // exactly what keeps the highlights alive across body re-renders.
    const observer = new MutationObserver(() => run())
    const run = () => {
      if (painter.mutatesDom) observer.disconnect()
      const hits = painter.apply(container, query)
      setCount(hits)
      setActiveIndex((prev) => (hits === 0 ? 0 : Math.min(prev, hits - 1)))
      if (painter.mutatesDom) {
        observer.observe(container, {
          childList: true,
          subtree: true,
          characterData: true,
        })
      }
    }
    run()
    if (!painter.mutatesDom) {
      observer.observe(container, {
        childList: true,
        subtree: true,
        characterData: true,
      })
    }

    return () => {
      observer.disconnect()
      painter.clear(container)
    }
  }, [containerRef, query, active])

  // Re-paint the active hit + scroll it into view whenever the index or
  // the hit set changes.
  useEffect(() => {
    if (count === 0) return
    painterRef.current?.setActive(activeIndex)
  }, [activeIndex, count])

  const next = useCallback(
    () => setActiveIndex((i) => (count === 0 ? 0 : (i + 1) % count)),
    [count],
  )
  const prev = useCallback(
    () => setActiveIndex((i) => (count === 0 ? 0 : (i - 1 + count) % count)),
    [count],
  )

  return { count, activeIndex, next, prev }
}
